import { useState, useEffect } from 'react';

interface FireLoadingScreenProps {
  message?: string;
  fullScreen?: boolean;
}

const LOADING_TIPS = [
  'Firing up the grill...',
  'Stoking the coals...',
  'Getting the smoke rolling...',
  'Warming up the pit...',
  'Loading the firebox...',
];

/**
 * Fire-themed loading screen using animated 🔥 emojis.
 * Pure CSS animations for zero-dependency performance.
 * Shown during initial post data loading on the homepage.
 */
export function FireLoadingScreen({ message, fullScreen = false }: FireLoadingScreenProps) {
  const [tipIndex, setTipIndex] = useState(Math.floor(Math.random() * LOADING_TIPS.length));
  const [fadeKey, setFadeKey] = useState(0);

  useEffect(() => {
    if (message) return;
    const interval = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % LOADING_TIPS.length);
      setFadeKey((prev) => prev + 1);
    }, 3000);
    return () => clearInterval(interval);
  }, [message]);

  const displayMessage = message || LOADING_TIPS[tipIndex];

  const containerClass = fullScreen
    ? 'fixed inset-0 z-[9999] flex items-center justify-center bg-black'
    : 'min-h-[60vh] w-full flex items-center justify-center bg-[#0a0a0a]';

  return (
    <div className={containerClass} role="status" aria-live="polite" aria-label="Loading content">
      <div className="flex flex-col items-center gap-6">
        {/* Flame emojis with CSS breathing animations */}
        <div className="relative flex items-end justify-center h-24 w-32">
          {/* Left flame */}
          <span className="absolute bottom-1 left-2 text-3xl animate-[flame-breathe_1.2s_ease-in-out_infinite_0.2s]">
            🔥
          </span>

          {/* Center flame — hero */}
          <span className="text-6xl z-10 animate-[flame-hero_2s_ease-in-out_infinite]">🔥</span>

          {/* Right flame */}
          <span className="absolute bottom-1 right-2 text-3xl animate-[flame-breathe_1.4s_ease-in-out_infinite_0.4s]">
            🔥
          </span>

          {/* Ambient glow under flames */}
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-16 h-3 rounded-full bg-orange-500/30 blur-md animate-[glow-pulse_2s_ease-in-out_infinite]" />
        </div>

        {/* Rotating message with crossfade */}
        <p
          key={fadeKey}
          className="text-sm text-gray-400 tracking-wide text-center animate-[fade-in-up_0.3s_ease-out]"
        >
          {displayMessage}
        </p>

        {/* Staggered dots */}
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="text-orange-500 text-xl animate-[dot-pulse_1.4s_ease-in-out_infinite]"
              style={{ animationDelay: `${i * 0.2}s` }}
            >
              •
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
